/**
 * Click handler behind useCalendlyPopup (#11843), kept free of React and the
 * DOM so its guard and retry cap are unit-testable.
 */

/** The `window.Calendly` global widget.js installs. */
export interface CalendlyGlobal {
  initPopupWidget: (opts: { url: string }) => void;
}

/** ~5 s at 100 ms: long enough for a slow widget.js, short enough to give up visibly. */
export const CALENDLY_MAX_ATTEMPTS = 50;
const RETRY_DELAY_MS = 100;

interface CalendlyOpenerDeps {
  ensureAssets: () => void;
  getCalendly: () => CalendlyGlobal | undefined;
  schedule: (task: () => void, delayMs: number) => void;
  /** Called when the widget never became available (blocked or offline). */
  onGiveUp: (url: string) => void;
  onLoadingChange: (loading: boolean) => void;
  maxAttempts?: number;
}

/**
 * The click handler, without React: loads the assets, polls for
 * `window.Calendly` up to `maxAttempts` times, and ignores further clicks
 * while one open is in flight (a double click would otherwise start two
 * polling loops and open two popups).
 */
export function createCalendlyOpener(
  url: string,
  {
    ensureAssets,
    getCalendly,
    schedule,
    onGiveUp,
    onLoadingChange,
    maxAttempts = CALENDLY_MAX_ATTEMPTS,
  }: CalendlyOpenerDeps
): () => void {
  let inFlight = false;
  const finish = () => {
    inFlight = false;
    onLoadingChange(false);
  };
  return () => {
    if (inFlight) return;
    inFlight = true;
    onLoadingChange(true);
    ensureAssets();
    let attempts = 0;
    const tryOpen = () => {
      attempts += 1;
      const calendly = getCalendly();
      if (calendly) {
        finish();
        calendly.initPopupWidget({ url });
        return;
      }
      if (attempts >= maxAttempts) {
        finish();
        onGiveUp(url);
        return;
      }
      schedule(tryOpen, RETRY_DELAY_MS);
    };
    tryOpen();
  };
}
